import React, { useState, useEffect } from "react";
import { withRouter } from "react-router-dom";
import { connect, useSelector, useDispatch } from "react-redux";
import { bindActionCreators } from "redux";
import { HeaderBlock } from "../../components/header-block/HeaderBlock";
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic'
import KeyboardArrowLeft from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight';

// Actions
import { getUsers, searchUsers, syncWithADUsers, GET_USER_SIZE } from "../../state/actions/UserActions";
import { Loading } from "../../components/loading/Loading";
import SimpleTable from "../../components/table/Table";

// Styles
import styles from "./UsersPage.module.scss";
import { TextField, Grid, Box, makeStyles, IconButton, useTheme, Button, InputAdornment, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Chip } from "@material-ui/core";
import { PagePlaceholder } from "../../components/page-placeholder/PagePlaceholder";
import { getIcon } from "../../icon/icon";
import PageContainer from "../../components/page-container/PageContainer";
import CancelIcon from '@material-ui/icons/Cancel';
import Search from "../../components/search/Search";
import { useTranslation } from "react-i18next";


const useStyles = makeStyles(theme => ({
  tablePaginationRoot: {
    flexShrink: 0,
    marginLeft: theme.spacing(2.5),
  },
}))

function UsersPage(props) {

  const users = useSelector(state => state.UserReducer.users)
  const continuationToken = useSelector(state => state.UserReducer.continuationToken)
  const instanceId = useSelector(state => state.UserReducer.instanceId)
  const statusOutput = useSelector(state => state.UserReducer.statusOutput)

  const [t, i18n] = useTranslation()
  const dispatch = useDispatch()
  const classes = useStyles(props)

  const rowsPerPage = [5, 15, 20, 25, 50]

  const [loading, setLoading] = useState(false)

  const [userData, setUserData] = useState([])
  const [fetchUsers, setFetchUsers] = useState(true)
  const [pageSize, setPageSize] = useState(GET_USER_SIZE.MEDIUM)
  const [useCT, setUseCT] = useState(false)
  const [currentPageNumber, setCurrentPageNumber] = useState(0)
  const [pageMap, setPageMap] = useState({ 0: null })
  const [searchText, setSearchText] = useState("")
  const [syncADModalOpen, setSyncADModalOpen] = useState(false)
  const [syncingUsers, setSyncingUsers] = useState(instanceId != null)
  const [syncFailed, setSyncFailed] = useState(false)


  useEffect(() => {
    if (fetchUsers) {
      setLoading(true)
      dispatch(getUsers(() => {
        setFetchUsers(false)
        setLoading(false)
      }, pageSize, useCT ? (searchText == "" ? pageMap[currentPageNumber]?.base : pageMap[currentPageNumber]?.search) : null, searchText))
    }

  }, [fetchUsers])

  useEffect(() => {
    setUseCT(true)
    updatePageMap(currentPageNumber, continuationToken)
  }, [continuationToken])

  useEffect(() => {
    const userData = createData(users)
    setUserData(userData)
  }, [users])

  useEffect(() => {
    setFetchUsers(true)
  }, [pageSize])

  useEffect(() => {
    if (instanceId == null) {
      setSyncingUsers(false)
    } else {
      setSyncingUsers(true)
    }
  }, [instanceId])

  useEffect(() => {
    if (statusOutput == "Failed" || statusOutput == "Cancelled" || statusOutput == "Terminated") {
      setSyncingUsers(false)
      setSyncFailed(true)
    } else {
      setSyncFailed(false)
    }
  }, [statusOutput])

  function updatePageMap(pageNumber, token) {
    const currentPageMap = Object.assign({}, pageMap)
    
    if (token?.base != null || token?.search != null) {
      currentPageMap[pageNumber + 1] = token
    }

    setPageMap(currentPageMap)
  }

  function handleSearch(value) {
    setLoading(true)
    setSearchText(value)
    setCurrentPageNumber(0)
    setPageMap({ 0: null })
    dispatch(getUsers(() => {
      setLoading(false)
    }, pageSize, null, value))
  }

  function handleStartSync() {
    setSyncADModalOpen(false)
    dispatch(syncWithADUsers(instanceId, () => {
      setSyncFailed(false)
    }))
  }

  function createData(users) {
    const userKeys = Object.keys(users)

    return userKeys.map(userKey => {
      const user = users[userKey]
      let routes = ""

      if (user.routes != null) {
        let others = Object.keys(user.routes).length;
        if (others > 2) {
          routes =
            Object.keys(user.routes)
              .slice(0, 2)
              .join(", ") + `, +${others - 2} ` + t("common.more")
        } else {
          routes = Object.keys(user.routes).join(", ")
        }
      }

      return {
        userId: user.id,
        name: user.name,
        route: routes,
        email: user.email
      };
    });
  }

  function createCells(headers) {
    let cells = [];

    headers.forEach(header => {
      let cell = {};

      cell.id = header.id;

      if (header.hasOwnProperty("action")) {
        cell.handleClick = (event, row) => {
          let path = header.action.path.replace(
            "{id}",
            row[header.action.identifier]
          );
          props.history.push(path);
        };
      }

      cell.align = header.align;
      cell.clickable = header.clickable;

      cells.push(cell);
    });

    return cells;
  }

  function createHeaders() {
    return [
      {
        id: "name",
        clickable: true,
        action: { path: "/users/{id}", identifier: "userId" },
        align: "left",
        label: t("common.name")
      },
      {
        id: "email",
        clickable: true,
        align: "left",
        label: t("common.email")
      },
      {
        id: "userId",
        align: "right",
        label: t("user.user-id")
      },
      {
        id: "route",
        clickable: false,
        action: { path: "/routes/{id}", identifier: "route" },
        align: "right",
        label: t("route.route")
      }
    ];
  }

  function renderData() {
    const headers = createHeaders();
    const cells = createCells(headers);

    if (userData.length === 0) {
      return (
        <PagePlaceholder
          image={getIcon("users.svg")}
          text={t("user.no-users")}
        />
      );
    } else {
      return (
        <SimpleTable
          dataId={"userId"}
          cells={cells}
          headers={headers}
          rows={userData}
          page={currentPageNumber}
          onChangePage={(event, pageNumber) => {
            setCurrentPageNumber(pageNumber)
            setFetchUsers(true)
          }}
          onChangeRowsPerPage={(event) => {
            setPageSize(event.target.value)
          }}
          rowsPerPageOptions={rowsPerPage}
          rowsPerPage={pageSize}
          customActions={TablePaginationActions}
          disableDisplayedRowsLabel
        />
      );
    }
  }

  function TablePaginationActions(props) {
    const theme = useTheme();
    const { count, page, rowsPerPage, onChangePage } = props;
    const handleBackButtonClick = (event) => {
      onChangePage(event, page - 1);
    };

    const handleNextButtonClick = (event) => {
      onChangePage(event, page + 1);
    };

    return (
      <Box className={classes.tablePaginationRoot}>
        <IconButton onClick={handleBackButtonClick} disabled={page == 0} aria-label="previous page">
          {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
        </IconButton>
        <IconButton
          onClick={handleNextButtonClick}
          disabled={continuationToken?.base == null && continuationToken?.search == null }
          aria-label="next page"
        >
          {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
        </IconButton>
      </Box>
    );
  }

  function syncADUsersModal() {
    return (
      <Dialog open={syncADModalOpen}>
        <DialogTitle>
          {t("user.sync-users-question")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("user.sync-users-description")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setSyncADModalOpen(false)}

          >
            {t("common.cancel")}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleStartSync}
          >
            {t("user.sync-users")}
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  return (
    <Box className={styles.container}>
      <HeaderBlock
        title={t("user.users")}
        right={
          <Grid container spacing={2} alignItems="center" justify="flex-end">
            <Grid item>
              {statusOutput != null ? <Chip label={t("common.last-sync-status", { status: statusOutput })} /> : null}
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => syncingUsers ? handleStartSync() : setSyncADModalOpen(true)}
              >
                {syncingUsers ? t("common.check-sync-status") : t("user.sync-with-ad")}
              </Button>
            </Grid>
            <Grid item>

              <Search
                searchTitle={t("user.search-users")}
                searchFunction={handleSearch}
                searchValue={searchText}
              />
            </Grid>
          </Grid>
        }
      />

      {syncADUsersModal()}

      <PageContainer hasFooter>
        {loading ? <Loading /> : renderData()}
      </PageContainer>
    </Box>
  )
}

export default UsersPage
